import { Button } from "@/ds/shadcn/components/ui/button";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import TCGplayerLogo from "@/assets/TCGplayer-logo.png";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { useOperationTcgPlayerMassEntryLink } from "@/features/collections/api/useOperationTcgPlayerMassEntryLink";
import { useNavigate, useParams } from "react-router";
import { CircleDollarSignIcon, CopyPlusIcon, FolderOpenIcon, HashIcon, ListIcon, MergeIcon, type LucideIcon } from "lucide-react";
import { useOperationDuplicate } from "@/features/collections/api/useOperationDuplicate";
import { useOperationNormaliseUnitAmount } from "@/features/collections/api/useOperationNormaliseUnitAmount";
import { useCollection } from "@/features/collections/api/useCollection";
import { cn } from "@/ds/utils";
import { Selector } from "@/ds/custom/Selector";
import { CurrencyOptions, type Currency } from "@/lib/domain/Currency";
import { usePreferredCurrency } from "@/features/common/api/usePreferredCurrency";
import { useOperationMerge } from "@/features/collections/api/useOperationMerge";
import { useOperationNormaliseQuantity } from "@/features/collections/api/useOperationNormaliseQuantity";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { DataView } from "@/features/common/components/dataview/DataView";
import { useCollections } from "@/features/collections/api/useCollections";
import { CollectionsSortOrderLabels } from "@/lib/domain/CollectionsSortOrder";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { SmallCollectionDisplay } from "@/features/common/components/SmallCollectionDisplay";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { SectionRow } from "@/ds/custom/SectionRow";
import { StatePanel } from "@/ds/custom/StatePanel";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";

type ModalToolOptions = "BuyWithTcgPlayer" | "Duplicate" | "NormaliseQuantity" | "NormaliseUnitAmount";
type RightPanelToolOptions = "Merge";

export const TabCollectionTools = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;
	const navigate = useNavigate();

	const [modalToolOption, setModalToolOption] = useState<ModalToolOptions | null>(null);
	const [rightPanelToolOption, setRightPanelToolOption] = useState<RightPanelToolOptions | null>(null);

	return (
		<>
			<SplitScreen fixed visible={!!rightPanelToolOption} onVisibleChange={visible => !visible && setRightPanelToolOption(null)}>
				<SplitScreenLeftPanel>
					<ToolOptionDisplay
						title="Buy with TCGplayer"
						description="Purchase this collection with TCGplayer"
						buttonText="Buy"
						image={TCGplayerLogo}
						onClick={() => setModalToolOption("BuyWithTcgPlayer")} />

					<ToolOptionDisplay
						title="Mass Transaction Entry"
						description="Record a box opening or trade with ease"
						buttonText="Mass Entry"
						icon={ListIcon}
						onClick={() => navigate(`/collections/${collectionId}/transactions/bulk`)} />

					<ToolOptionDisplay
						title="Duplicate Collection"
						description="Duplicate an existing collection"
						buttonText="Duplicate"
						icon={CopyPlusIcon}
						onClick={() => setModalToolOption("Duplicate")} />

					<ToolOptionDisplay
						title="Merge Collection"
						description="Add another collection's transactions into this collection"
						buttonText="Merge"
						icon={MergeIcon}
						onClick={() => setRightPanelToolOption("Merge")} />

					<ToolOptionDisplay
						title="Normalize Quantity"
						description="Set all transaction quantities to a specific value"
						buttonText="Normalize"
						icon={HashIcon}
						onClick={() => setModalToolOption("NormaliseQuantity")} />

					<ToolOptionDisplay
						title="Normalize Unit Amount"
						description="Set all transaction unit amounts to a specific value"
						buttonText="Normalize"
						icon={CircleDollarSignIcon}
						onClick={() => setModalToolOption("NormaliseUnitAmount")} />
				</SplitScreenLeftPanel>
				<SplitScreenRightPanel>
					{
						rightPanelToolOption === "Merge" &&
						<MergeCollection collectionId={collectionId} />
					}
				</SplitScreenRightPanel>
			</SplitScreen>

			<ModalBuyWithTcgPlayer
				collectionId={collectionId}
				open={modalToolOption === "BuyWithTcgPlayer"}
				onOpenChange={open => !open && setModalToolOption(null)} />

			<ModalDuplicateCollection
				collectionId={collectionId}
				open={modalToolOption === "Duplicate"}
				onOpenChange={open => !open && setModalToolOption(null)} />

			<ModalNormaliseQuantity
				collectionId={collectionId}
				open={modalToolOption === "NormaliseQuantity"}
				onOpenChange={open => !open && setModalToolOption(null)} />

			<ModalNormaliseUnitAmount
				collectionId={collectionId}
				open={modalToolOption === "NormaliseUnitAmount"}
				onOpenChange={open => !open && setModalToolOption(null)} />
		</>
	);
};

interface ToolOptionDisplayProps {
	title: string;
	description: string;
	buttonText: string;
	icon?: LucideIcon;
	image?: string;
	onClick: () => void;
}

const ToolOptionDisplay = (props: ToolOptionDisplayProps): JSX.Element => {
	const {
		title,
		description,
		buttonText,
		icon,
		image,
		onClick
	} = props;

	return (
		<SectionRow icon={icon} leading={image && <img src={image} className="size-5 shrink-0" />} title={title} description={description}>
			<Button className="w-full @md:w-24" variant="outline" onClick={onClick}>
				{buttonText}
			</Button>
		</SectionRow>
	);
};

interface ModalProps {
	collectionId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const ModalBuyWithTcgPlayer = (props: ModalProps) => {
	const {
		collectionId,
		open,
		onOpenChange
	} = props;

	const tcgPlayerMassEntryLink = useOperationTcgPlayerMassEntryLink();

	useEffect(() => {
		if (open) {
			tcgPlayerMassEntryLink.mutate(collectionId);
		}
	}, [open]);

	const handleOnConfirm = useCallback(() => {
		if (!tcgPlayerMassEntryLink.data) {
			return;
		}

		window.open(tcgPlayerMassEntryLink.data.body.tcgPlayerMassEntryLink, "_blank", "noopener, noreferrer");
		onOpenChange(false);
	}, [tcgPlayerMassEntryLink.data, onOpenChange]);

	return (
		<ModalConfirm
			open={open}
			onOpenChange={onOpenChange}
			onConfirm={handleOnConfirm}
			title="Buy With TCGplayer"
			description="Click the button below to add all products in this collection to your cart with TCGplayer. Up to 125 products can be added this way."
			confirmText="Add"
			error={tcgPlayerMassEntryLink.error}
			isLoading={tcgPlayerMassEntryLink.isPending} />
	);
};

const ModalDuplicateCollection = (props: ModalProps) => {
	const {
		collectionId,
		open,
		onOpenChange
	} = props;

	const duplicateOperation = useOperationDuplicate();
	useNotifyMutationSuccess(duplicateOperation, "Collection Duplicated");
	const navigate = useNavigate();

	useEffect(() => {
		if (duplicateOperation.data) {
			navigate(`/collections/${duplicateOperation.data.body.collectionId}`);
		}
	}, [duplicateOperation.data]);

	return (
		<ModalConfirm
			open={open}
			onOpenChange={open => {
				if (!open) {
					duplicateOperation.reset();
				}

				onOpenChange(open);
			}}
			onConfirm={() => duplicateOperation.mutate(collectionId)}
			title="Duplicate Collection"
			description="Are you sure you want to duplicate this collection?"
			error={duplicateOperation.error}
			isLoading={duplicateOperation.isPending} />
	);
};

const ModalNormaliseQuantity = (props: ModalProps) => {
	const {
		collectionId,
		open,
		onOpenChange
	} = props;

	const [normalisedQuantity, setNormalisedQuantity] = useState(0);
	const normaliseQuantityOperation = useOperationNormaliseQuantity();
	useNotifyMutationSuccess(normaliseQuantityOperation, "Quantities Normalized");
	const navigate = useNavigate();

	useEffect(() => {
		if (open) {
			setNormalisedQuantity(0);
		}
	}, [open]);

	useEffect(() => {
		if (normaliseQuantityOperation.isSuccess) {
			onOpenChange(false);
			navigate("..");
		}
	}, [normaliseQuantityOperation.isSuccess]);

	const onConfirm = useCallback(() => {
		normaliseQuantityOperation.mutate({
			requestModel: {
				normalisedQuantity
			},
			collectionId
		});
	}, [collectionId, normalisedQuantity, normaliseQuantityOperation]);

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={normaliseQuantityOperation.isPending}>
			<ModalHeader>
				<ModalTitle>Normalize Quantity</ModalTitle>
				<ModalDescription>Enter the quantity you would like to set for all your transactions.</ModalDescription>
			</ModalHeader>
			<ModalBody>
				<div className="flex flex-col gap-4">
					<NumericInput
						value={normalisedQuantity}
						onChange={setNormalisedQuantity}
						maxDecimalPlaces={0}
						minValue={0}
						maxValue={100000} />

					<ErrorDisplay error={normaliseQuantityOperation.error} />
				</div>
			</ModalBody>
			<ModalFooter>
				<Button variant="outline" onClick={() => onOpenChange(false)} disabled={normaliseQuantityOperation.isPending}>
					Cancel
				</Button>
				<Button variant="destructive" onClick={onConfirm} disabled={!normalisedQuantity || normaliseQuantityOperation.isPending}>
					{normaliseQuantityOperation.isPending ? <Spinner /> : <>Confirm</>}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

const ModalNormaliseUnitAmount = (props: ModalProps) => {
	const {
		collectionId,
		open,
		onOpenChange
	} = props;

	const preferredCurrency = usePreferredCurrency();
	const [normalisedUnitAmount, setNormalisedUnitAmount] = useState(0);
	const [currency, setCurrency] = useState<Currency>(preferredCurrency.data?.currency ?? "USD");
	const normaliseUnitAmountOperation = useOperationNormaliseUnitAmount();
	useNotifyMutationSuccess(normaliseUnitAmountOperation, "Unit Amounts Normalized");
	const navigate = useNavigate();

	useEffect(() => {
		if (open) {
			setNormalisedUnitAmount(0);
			setCurrency(preferredCurrency.data?.currency ?? "USD");
		}
	}, [open]);

	useEffect(() => {
		if (normaliseUnitAmountOperation.isSuccess) {
			onOpenChange(false);
			navigate("..");
		}
	}, [normaliseUnitAmountOperation.isSuccess]);

	const onConfirm = useCallback(() => {
		normaliseUnitAmountOperation.mutate({
			requestModel: {
				normalisedUnitAmount: {
					value: normalisedUnitAmount,
					currency
				}
			},
			collectionId
		});
	}, [collectionId, normalisedUnitAmount, currency, normaliseUnitAmountOperation]);

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={normaliseUnitAmountOperation.isPending}>
			<ModalHeader>
				<ModalTitle>Normalize Unit Amount</ModalTitle>
				<ModalDescription>Enter the unit amount you would like to set for all your transactions.</ModalDescription>
			</ModalHeader>
			<ModalBody>
				<div className="flex flex-col gap-4">
					<div className="flex gap-2">
						<NumericInput
							value={normalisedUnitAmount}
							onChange={setNormalisedUnitAmount}
							maxDecimalPlaces={2}
							minValue={0}
							maxValue={100000} />
						<Selector
							placeholder="Currency"
							options={CurrencyOptions}
							value={currency}
							onChange={setCurrency} />
					</div>

					<ErrorDisplay error={normaliseUnitAmountOperation.error} />
				</div>
			</ModalBody>
			<ModalFooter>
				<Button variant="outline" onClick={() => onOpenChange(false)} disabled={normaliseUnitAmountOperation.isPending}>
					Cancel
				</Button>
				<Button variant="destructive" onClick={onConfirm} disabled={normalisedUnitAmount < 0 || normaliseUnitAmountOperation.isPending}>
					{normaliseUnitAmountOperation.isPending ? <Spinner /> : <>Confirm</>}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

interface MergeCollectionProps {
	collectionId: string;
}

const MergeCollection = (props: MergeCollectionProps) => {
	const {
		collectionId
	} = props;

	const collection = useCollection(collectionId);
	const mergeOperation = useOperationMerge();
	useNotifyMutationSuccess(mergeOperation, "Collections Merged");
	const collections = useCollections(CollectionsSortOrderLabels.Alphabetical, null);
	const [mergeCollectionId, setMergeCollectionId] = useState<string | null>(null);
	const [modalMergeCollection, setModalMergeCollection] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (mergeOperation.isSuccess) {
			setMergeCollectionId(null);
			setModalMergeCollection(false);
			navigate("..");
		}
	}, [mergeOperation.isSuccess]);

	const performMergeOperation = useCallback(() => {
		if (!collection.data?.collectionId || !mergeCollectionId) {
			return;
		}

		mergeOperation.mutate(
			{
				requestModel: {
					mergeCollectionId: mergeCollectionId
				},
				collectionId: collection.data?.collectionId
			}
		);
	}, [collection.data?.collectionId, mergeCollectionId, mergeOperation]);

	const collectionName = useMemo(() => collection.data?.name, [collection.data?.name]);
	const hasNoOtherCollections = useMemo(() => {
		if (!collections.isSuccess || collections.hasNextPage) {
			return false;
		}

		return collections.data.pages.every(x => x.results.every(y => y.collectionId === collectionId));
	}, [collections.isSuccess, collections.hasNextPage, collections.data, collectionId]);
	const onSelectedCollection = useCallback((collectionId: string) => {
		setMergeCollectionId(collectionId);
		setModalMergeCollection(true);
	}, [setMergeCollectionId, setModalMergeCollection]);

	return (
		<>
			<SectionRow
				title="Merge Collection"
				description={collectionName ? `Select the collection you want to merge into ${collectionName}.` : "Select the collection you want to merge into this one."} />

			{
				hasNoOtherCollections ? (
					<StatePanel
						icon={FolderOpenIcon}
						title="No Collections"
						description="Create another collection to merge it into this one." />
				) : (
					<DataView
						forQuery={collections}
						getKey={x => x.collectionId}
						listViewHeaderLabels={["Collection"]}
						listViewHeaderClassNames={["min-w-0"]}
						listComponent={MergeCollectionListView}
						additionalProps={{
							selectedCollectionId: mergeCollectionId,
							currentCollectionId: collectionId,
							onSelectCollection: onSelectedCollection
						}} />
				)
			}

			<ModalConfirm
				open={modalMergeCollection}
				onOpenChange={open => {
					if (!open) {
						mergeOperation.reset();
					}

					setModalMergeCollection(open);
				}}
				onConfirm={performMergeOperation}
				title="Merge Collection"
				description="Are you sure you want to merge this collection? This will add all transactions from the selected collection into this one."
				confirmDestructive
				error={mergeOperation.error}
				isLoading={mergeOperation.isPending} />
		</>
	);
};

interface MergeCollectionListViewProps extends CollectionModel {
	selectedCollectionId: string | null;
	currentCollectionId: string;
	onSelectCollection: (collectionId: string) => void;
}

const MergeCollectionListView = (props: MergeCollectionListViewProps) => {
	const {
		collectionId,
		name,
		description,
		image,
		selectedCollectionId,
		currentCollectionId,
		onSelectCollection
	} = props;

	const selected = useMemo(() => selectedCollectionId === collectionId, [selectedCollectionId, collectionId]);

	if (collectionId === currentCollectionId) {
		return <></>;
	}

	return (
		<ListItem onClick={() => onSelectCollection(collectionId)} className={cn(selected && "bg-background-highlight")}>
			<TableCell>
				<SmallCollectionDisplay name={name} description={description} image={image} />
			</TableCell>
		</ListItem>
	);
};
